import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@apollo/client';
import styled from 'styled-components/native';

import DataState from 'app/src/components/DataState';
import { Text } from 'app/src/styles';
import * as colors from 'app/src/styles/colors';
import { dollars, numberWithDelimiter } from 'app/src/utils/stringHelpers';
import Modal from 'app/src/elements/Modal';

import {
  SaleAssetContainer,
  BorderedContainer,
  Title,
  Going,
  GoingText,
  goingColor,
} from './elements';
import { BIDDING_SALE_ASSETS } from './queries';
import { SaleAsset, isActive } from './SaleAssets';
import AnnouncementIcon from '../Sales/Announcements/AnnouncementIcon';

const Container = styled(View)`
  height: 100%;
  flex: 1;
  overflow: auto;
`;

const Thumbnail = styled(View)`
  height: 150px;
  width: 240px;
  align-items: center;
  text-align: center;
  overflow: clip;
`;

const CurrentSaleAssetContainer = styled(SaleAssetContainer)`
  border-top-width: 2px;
  border-top-style: solid;
  border-top-color: ${colors.elRed};
  border-bottom-width: 2px;
  border-bottom-style: solid;
  border-bottom-color: ${colors.elRed};
  gap: 10px;
`;

const BoldText = styled(Text)`
  font-weight: 600;
`;

const PriorAssetText = styled(Text)`
  font-size: 20px;
  font-weight: 600;
`;


const groupedWith = (saleAsset, saleAssets) => {
  if (!saleAsset.pendingBidGroupId) { return null; }

  return saleAssets.filter(entry => (
    entry.pendingBidGroupId === saleAsset.pendingBidGroupId
  ));
};

const PriorAsset = ({ sale }) => {
  const saleAsset = sale.priorSaleAsset;

  if (!saleAsset) {
    return <Text>No Prior Asset</Text>
  }

  let biddingStatus = saleAsset.biddingStatus;
  if (saleAsset.pendingSellerConfirmation) {
    biddingStatus = 'Pending Seller Confirmation';
  }

  return (
    <View style={{ marginBottom: 20 }}>
      <PriorAssetText>
        Prior Asset: {saleAsset.lotNumber}
        {' - '}{biddingStatus}
      </PriorAssetText>
      {'Sold' === saleAsset.biddingStatus && (
        <PriorAssetText>
          {dollars(saleAsset.biddingSoldAmount)}
          {' - '}{saleAsset.biddingSoldBuyerDetails}
        </PriorAssetText>
      )}
    </View>
  );
};

export const CurrentSaleAsset = ({ saleAsset, saleAssets, highBid,
  setSaleAssetAnnouncements, setAnnouncementAssetDetails
}) => {
  const { asset } = saleAsset;

  const inGroup = groupedWith(saleAsset, saleAssets);

  return (
    <CurrentSaleAssetContainer>
      <Thumbnail>
        {asset.primaryPhotoUrl && (
          <img
            src={asset.primaryPhotoUrl}
            alt={asset.name}
            style={{flex: 1, maxHeight: '100%'}}
          />
        )}
      </Thumbnail>
      <View style={{flex: 1}}>
        {goingColor(saleAsset) && (
          <Going style={{ backgroundColor: goingColor(saleAsset) }}>
            <GoingText>{saleAsset.biddingStatus}</GoingText>
          </Going>
        )}
        {inGroup && (
          <Title style={{backgroundColor: 'yellow'}}>
            Choice:{' '}
            {inGroup.map(entry => entry.lotNumber).join(' ')}
          </Title>
        )}
        <View style={{display: 'flex', flexDirection: 'row'}}>
          <Title>{saleAsset.lotNumber}</Title>
          <AnnouncementIcon
            saleAsset={saleAsset}
            setSaleAssetAnnouncements={setSaleAssetAnnouncements}
            setAnnouncementAssetDetails={setAnnouncementAssetDetails}
          />
        </View>
        <Text>{asset.name}</Text>
        <Text>{asset.vin}</Text>
        <Text>{asset.cityState?.toUpperCase()}</Text>
        {!!highBid && (
          <BoldText>CB: {numberWithDelimiter(highBid)}</BoldText>
        )}
      </View>
    </CurrentSaleAssetContainer>
  );
};

const CurrentSaleAssets = ({ sale }) => {
  const [saleAssetAnnouncements, setSaleAssetAnnouncements] = useState(false);
  const [announcementAssetDetails, setAnnouncementAssetDetails] = useState(false);
  const { data } = useQuery(BIDDING_SALE_ASSETS, {
    variables: { saleId: sale.id }
  });

  const activeRef = useCallback(element => {
    if (element) {
      element.scrollIntoView();
    }
  }, []);

  if (!data) { return <DataState.Loading />; }

  const currentIndex = data.saleAssets.findIndex(item => isActive(sale, item));
  const highBid = data.saleAssets[currentIndex]?.highBid &&
    parseInt(data.saleAssets[currentIndex].highBid);

  const saleAssets = data.saleAssets;

  return (
    <>

      <BorderedContainer>
        <PriorAsset sale={sale} />
        <Container>
          {saleAssets.map(saleAsset => (
            <React.Fragment key={saleAsset.id}>
              {isActive(sale, saleAsset) ? (
                <>
                  <div ref={activeRef}>
                  </div>
                  <CurrentSaleAsset
                    saleAsset={saleAsset}
                    saleAssets={data.saleAssets}
                    highBid={highBid}
                    setSaleAssetAnnouncements={setSaleAssetAnnouncements}
                    setAnnouncementAssetDetails={setAnnouncementAssetDetails}
                  />
                </>
              ) : (
                <SaleAsset saleAsset={saleAsset} />
              )}
            </React.Fragment>
          ))}
          <View style={{ height: '80vh' }}></View>
        </Container>
      </BorderedContainer>
      {saleAssetAnnouncements &&
        <Modal
          showModal={saleAssetAnnouncements}
          setShowModal={setSaleAssetAnnouncements}
          modalType={`${announcementAssetDetails} Announcements`}
        >
          <View>
            {
              saleAssetAnnouncements.map(announcement => {
                return <View style={{margin: '10px'}}>
                  {announcement.body}
                </View>
              })
            }
          </View>
        </Modal>
      }
    </>
  );
};

export default CurrentSaleAssets;
